/**
 * @fileoverview PortPuffin Privacy Policy registry data.
 *
 * Port-web has no existing duplicate source — its
 * `apps/port-web/src/portals/marketing-website/pages/` directory
 * is empty (pre-existing gap from initial port-web scaffold). Content
 * is authored fresh in the cross-tenant ecosystem voice modeled on
 * sibling apps (planner-web's structure, adapted to PortPuffin's
 * voice + AI receptionist domain — call recordings, voicemail
 * transcripts, ASR/LLM/TTS pipelines, emergency keyword detection).
 *
 * Includes a port-only "Voice & Recording" section (#4) covering the
 * specific data flows that PortPuffin handles (recorded calls,
 * transcripts, voicemails, AI-generated responses) — these are the
 * sensitive data types unique to a voice platform.
 */

/** @type {import('../../types.js').LegalDocument} */
export const privacy = Object.freeze({
  appKey: 'port',
  documentKey: 'privacy',
  frontmatter: {
    title: 'Privacy Policy',
    productName: 'PortPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-05-04',
    contactEmail: 'privacy@portpuffin.com',
    canonicalUrl: 'https://portpuffin.com/privacy',
    ogImage: 'https://portpuffin.com/og-image.jpg',
    description:
      'PortPuffin privacy policy. Learn how we collect, use, and protect your call recordings, voicemails, transcripts, and personal information in our business voice and AI receptionist platform.',
  },
  sections: [
    {
      id: 'information-we-collect',
      title: '1. Information We Collect',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'When you use {productName}, we collect information you provide directly, including your business name, email address, phone numbers you provision, IVR configuration, and contact directory necessary to operate your business voice service.',
        },
        {
          type: 'paragraph',
          text: 'When callers reach your phone numbers, we capture the call metadata required to route the call (caller phone number, dialed number, timestamp, duration, status), and — where you have enabled the relevant features — call recordings, voicemail audio + transcripts, and ASR transcripts of caller speech. Recording is gated by per-state two-party-consent law and surfaces a recorded announcement before audio is captured.',
        },
        {
          type: 'paragraph',
          text: 'We also automatically collect usage data such as IP addresses, browser type, pages visited, and timestamps to improve our services and ensure security.',
        },
      ],
    },
    {
      id: 'how-we-use-your-information',
      title: '2. How We Use Your Information',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: 'We use your information to route inbound calls per your IVR configuration, deliver voicemails and transcripts, drive the AI receptionist (including ASR, intent classification, LLM orchestration, and TTS playback), generate analytics, and communicate service updates and recovered savings to you.',
        },
        {
          type: 'paragraph',
          text: 'We may also use your information to send service updates, respond to inquiries, improve our routing and AI algorithms, detect emergency keywords for safety alerting, and comply with legal obligations.',
        },
      ],
    },
    {
      id: 'data-security',
      title: '3. Data Security',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'We implement industry-standard security measures including encryption in transit (TLS), AES-256-GCM encryption at rest for call recordings and transcripts, KMS-managed encryption keys, per-company data isolation (a separate database schema for each company), access controls, append-only audit logging, and regular security audits to protect your call data and personal information.',
        },
      ],
    },
    {
      id: 'voice-and-recording',
      title: '4. Voice, Recording, and Transcription',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: 'When recording is enabled for a phone number, {productName} prepends a state-aware consent announcement to the call before audio is captured. We follow per-state law for two-party-consent jurisdictions (CA, FL, IL, MD, MA, MI, MT, NV, NH, OR, PA, VT, WA, plus DC) and one-party-consent jurisdictions (the remaining 37 states). Recordings are encrypted at rest and retained per your configured retention policy, with a default of 90 days.',
        },
        {
          type: 'paragraph',
          text: 'Voicemail audio is transcribed via third-party transcription providers (Deepgram, AWS Transcribe). ASR transcripts of in-call caller speech are encrypted at rest using AES-256-GCM. Transcripts may be redacted or hard-deleted at any time via the {productName} admin console, subject to legal-hold guards.',
        },
        {
          type: 'paragraph',
          text: 'Voice cloning is supported only with explicit attestation of rights and is watermarked with PerTH per industry best practice. Cloned voices are subject to DMCA hard-delete on rights challenge.',
        },
      ],
    },
    {
      id: 'tcpa-and-dnc-compliance',
      title: '5. TCPA, DNC, and Per-Channel Consent',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} enforces TCPA + FCC compliance on outbound contact via a federated Do-Not-Call (DNC) list with five independent sources: the federal DNC registry, per-state registries for all 50 states + DC, your own internal DNC list, customer-initiated stop requests, and SMS STOP-keyword enrollments. Before any outbound call or SMS is dispatched, a six-step gate algorithm checks every applicable source.',
        },
        {
          type: 'paragraph',
          text: 'Consent records are stored per channel (voice, SMS, fax) and per trigger (purpose). Each consent record retains the disclosure language presented at capture time and a cryptographic snapshot of the consent form source, so consent claims are independently verifiable. Companies that use the platform consent-capture builder (in-store kiosk, web widget, SMS-reply, verbal-recorded, paper-form, and widget-embed sources) cannot delete the disclosed language post-grant — the disclosed text is the customer-facing contractual record.',
        },
        {
          type: 'paragraph',
          text: 'Every block decision logged by the six-step gate is recorded in an append-only audit trail and is exportable on subpoena within a 60-second response time. The platform supports GDPR Article-15 access requests, CCPA right-to-know + right-to-delete requests, and FCC / TCPA-plaintiff discovery requests via the same evidence-export endpoint. A company that explicitly disables DNC enforcement assumes liability for the resulting outbound traffic.',
        },
        {
          type: 'paragraph',
          text: 'Per Phase 20 privacy invariant: full E.164 customer phone numbers are stored only in durable, encrypted storage with strict access controls. On the observability bus (Loki / Mimir / Tempo) only the last four digits are emitted, with the remainder redacted as `customerE164Redacted`. This is enforced regardless of your configuration.',
        },
        {
          type: 'paragraph',
          text: 'Outbound contact to recipients whose locale is unknown defaults to BLOCK (fail-OPEN flag default = false for unknown locales) — preventing accidental cross-border TCPA / GDPR / PECR violations. Your company may opt in to international outbound dispatch per recipient locale.',
        },
      ],
    },
    {
      id: 'data-sharing',
      title: '6. Data Sharing',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'We do not sell your personal information. We share call data with CPaaS providers (Twilio, Telnyx) solely for the purpose of completing call routing and number provisioning. We share transcript data with transcription and AI providers (Deepgram, AWS, Anthropic, ElevenLabs) solely for the purpose of generating transcripts and AI receptionist responses. We may also share data with service providers who assist in operating our platform under strict confidentiality agreements.',
        },
      ],
    },
    {
      id: 'data-retention',
      title: '7. Data Retention',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'We retain your data for as long as your account is active or as needed to provide services. Call recordings and voicemails follow your configured retention policy (default 90 days). Transcripts default to 365 days for ASR sessions. Audit and operational metadata is retained per regulatory requirements.',
        },
      ],
    },
    {
      id: 'your-rights',
      title: '8. Your Rights',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'You have the right to access, correct, or delete your personal information. You may also request a copy of your data, redact specific transcripts, or opt out of non-essential communications. To exercise these rights, contact us at the address below.',
        },
      ],
    },
    {
      id: 'cookies-and-analytics',
      title: '9. Cookies and Analytics',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'We use cookies and similar technologies to analyze site traffic and improve your experience. You can manage cookie preferences through our cookie consent banner. Analytics data is collected only with your consent.',
        },
      ],
    },
    {
      id: 'changes-to-this-policy',
      title: '10. Changes to This Policy',
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may update this privacy policy from time to time. We will notify you of significant changes by email or through a notice on our website. Continued use of our services after changes constitutes acceptance of the updated policy.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '11. Contact Us',
      order: 11,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about this privacy policy or our data practices, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
